import React from 'react';
import { useTranslation } from 'utils/i18n';
import marketplaceTranslations from './translations';
import '../ResourceDetailModal/styles/MarketplaceStepIndicator.css';

/**
 * Marketplace Step Indicator
 * Shows the 3-step process: Select Product → Configuration → Review
 * Review is a tab inside Configuration, but shown as step 3 when active
 */
export const MarketplaceStepIndicator = ({ 
  currentStep = 1,
  selectedProduct = null,
  selectedPrice = null
}) => {
  const { language } = useTranslation();
  const t = (key, defaultValue = '') => {
    const keys = key.split('.');
    let value = marketplaceTranslations[language] || marketplaceTranslations.en;
    for (const k of keys) {
      value = value?.[k];
    }
    return value || defaultValue || key;
  };
  // Determine which step is active
  // Step 1: Select Product (marketplace)
  // Step 2: Configuration (Basics tab)
  // Step 3: Review (Review tab inside Configuration)
  
  const getActiveStep = () => {
    // Step numbers: 1 = Marketplace, 2 = Configuration, 3 = Review
    // Visual: 1 = Select Product, 2 = Configuration, 3 = Review
    if (currentStep >= 1 && currentStep <= 3) {
      return currentStep;
    }
    return 1; // Default to step 1
  };
  
  const activeStep = getActiveStep();
  
  // Build steps array - 3 steps
  const steps = [
    { 
      id: 1, 
      label: t('steps.selectProduct', 'Select Product'), 
      completed: selectedProduct !== null,
      active: activeStep === 1
    },
    { 
      id: 2, 
      label: t('steps.configuration', 'Configuration'), 
      completed: activeStep > 2, // Completed if we're past this step
      active: activeStep === 2
    },
    { 
      id: 3, 
      label: t('steps.review', 'Review'), 
      completed: false,
      active: activeStep === 3
    }
  ];
  
  // Visual step numbers are simply 1, 2, 3 based on position
  const getVisualStepNumber = (index) => {
    return index + 1;
  };
  
  const visualActiveStep = activeStep; // Already mapped correctly
  
  return (
    <div className="marketplace-step-indicator">
      {steps.map((step, index) => {
        const visualStepNum = getVisualStepNumber(index);
        const isActive = step.active;
        // A step is past if its visual number is less than the active visual step
        const isPast = visualStepNum < visualActiveStep;
        // A step is completed if we're past it (not active and before current step)
        const isCompleted = isPast && !isActive;
        
        return (
          <React.Fragment key={step.id}>
            <div 
              className={`marketplace-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${isPast ? 'past' : ''}`}
            >
              <div className="marketplace-step-number">
                {isCompleted ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                ) : (
                  visualStepNum
                )}
              </div>
              <div className="marketplace-step-label">{step.label}</div>
            </div>
            {index < steps.length - 1 && (
              <div className={`marketplace-step-separator ${isCompleted || isPast ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

